/**
 * Bouwen: kosten betalen, plaatsen en punten tellen.
 *
 * Grondstoffen liggen in dit spel niet in een voorraad maar **op de landschappen**.
 * Betalen betekent dus: symbolen van je eigen landschappen afhalen (handboek p. 14).
 * Welk landschap je aanwijst maakt uit zodra je meer van een soort hebt dan je nodig
 * hebt — daarom is de betaling een keuze van de speler, geen automatisme.
 *
 * Bronnen: kennisbank 02 §2.2, handboek p. 13-14, fysieke regels 1996.
 */
import {
  LANDSCAPE_RESOURCE,
  LIMITS,
  type CardCost,
  type Resource,
  type Seat,
} from '@catan/contracts';
import type {
  Building,
  BuildingKind,
  LandscapeTile,
  PlayerState,
  Principality,
  Side,
  StartLandschap,
} from './state.js';
import { freeSlots, slotsPerSide } from './state.js';
import { actionCardName } from './actioncards.js';

/** Aanwijzing van één landschap: welke kolom, welke kant. */
export interface TileRef {
  readonly column: number;
  readonly side: Side;
}

/** De betaling: per aangewezen landschap gaat er één grondstof af. */
export type Payment = readonly TileRef[];

/** Wat een speler op dit moment aan grondstoffen heeft, per soort. */
export function resourcePool(player: PlayerState): Record<Resource, number> {
  const pool = { wol: 0, erts: 0, hout: 0, goud: 0, graan: 0, klei: 0 };
  for (const tile of player.principality.landscapes) {
    pool[LANDSCAPE_RESOURCE[tile.landscape]] += tile.resources;
  }
  return pool;
}

/** Kan deze speler zich dit veroorloven? */
export function canAfford(player: PlayerState, cost: CardCost): boolean {
  const pool = resourcePool(player);
  for (const [resource, amount] of Object.entries(cost) as [Resource, number][]) {
    if (pool[resource] < amount) return false;
  }
  return true;
}

/**
 * Controleert of een betaling precies klopt: de aangewezen landschappen moeten bestaan,
 * genoeg grondstoffen dragen, en samen exact de kosten dekken — niet meer, niet minder.
 */
export function checkPayment(
  player: PlayerState,
  cost: CardCost,
  payment: Payment,
): { ok: true } | { ok: false; reason: string } {
  const geteld: Partial<Record<Resource, number>> = {};
  const gebruikt = new Map<string, number>();

  for (const ref of payment) {
    const tile = player.principality.landscapes.find(
      (t) => t.column === ref.column && t.side === ref.side,
    );
    if (tile === undefined) {
      return { ok: false, reason: `geen landschap op kolom ${ref.column} (${ref.side})` };
    }
    const sleutel = `${ref.column}:${ref.side}`;
    const alGebruikt = (gebruikt.get(sleutel) ?? 0) + 1;
    gebruikt.set(sleutel, alGebruikt);
    if (alGebruikt > tile.resources) {
      return { ok: false, reason: `er liggen niet genoeg grondstoffen op kolom ${ref.column}` };
    }
    const resource = LANDSCAPE_RESOURCE[tile.landscape];
    geteld[resource] = (geteld[resource] ?? 0) + 1;
  }

  for (const [resource, amount] of Object.entries(cost) as [Resource, number][]) {
    if ((geteld[resource] ?? 0) !== amount) {
      return {
        ok: false,
        reason: `betaling klopt niet: ${geteld[resource] ?? 0}× ${resource} in plaats van ${amount}`,
      };
    }
  }
  for (const [resource, amount] of Object.entries(geteld) as [Resource, number][]) {
    if (amount > 0 && (cost as Record<string, number | undefined>)[resource] === undefined) {
      return { ok: false, reason: `${resource} hoort niet bij deze kosten` };
    }
  }
  return { ok: true };
}

/** Voert de betaling uit. Roep eerst `checkPayment` aan. */
export function pay(player: PlayerState, payment: Payment): PlayerState {
  const teBetalen = new Map<string, number>();
  for (const ref of payment) {
    const sleutel = `${ref.column}:${ref.side}`;
    teBetalen.set(sleutel, (teBetalen.get(sleutel) ?? 0) + 1);
  }
  const landscapes = player.principality.landscapes.map((tile) => {
    const aantal = teBetalen.get(`${tile.column}:${tile.side}`) ?? 0;
    return aantal === 0 ? tile : { ...tile, resources: tile.resources - aantal };
  });
  return { ...player, principality: { ...player.principality, landscapes } };
}

// ---------------------------------------------------------------- plaatsen

/** Aantal bouwwerken van een soort. */
export function countBuildings(principality: Principality, kind: BuildingKind): number {
  return principality.buildings.filter((b) => b.kind === kind).length;
}

/**
 * De kolommen waar een nieuw dorp kan komen.
 *
 * Een vorstendom groeit naar buiten: aan weerszijden van de rij kan een straat komen
 * met daarachter een dorp. De straat moet er eerst zijn (handboek p. 14: één straat
 * scheidt elk bouwwerk).
 */
export function settlementSpots(principality: Principality): number[] {
  const kolommen = principality.buildings.map((b) => b.column);
  const links = Math.min(...kolommen);
  const rechts = Math.max(...kolommen);
  const straten = new Set(principality.roads.map((r) => r.column));

  const spots: number[] = [];
  if (straten.has(links - 1)) spots.push(links - 2);
  if (straten.has(rechts + 1)) spots.push(rechts + 2);
  return spots;
}

/** De kolommen waar een nieuwe straat kan komen: direct naast de buitenste bouwwerken. */
export function roadSpots(principality: Principality): number[] {
  const kolommen = principality.buildings.map((b) => b.column);
  const bezet = new Set(principality.roads.map((r) => r.column));
  return [Math.min(...kolommen) - 1, Math.max(...kolommen) + 1].filter((c) => !bezet.has(c));
}

/** Mag hier een uitbreiding bij? */
export function canPlaceExpansion(
  building: Building,
  side: Side,
): { ok: true } | { ok: false; reason: string } {
  if (freeSlots(building)[side] <= 0) {
    return {
      ok: false,
      reason: `geen vrije plaats ${side} dit ${building.kind} (${slotsPerSide(building.kind)} per zijde)`,
    };
  }
  return { ok: true };
}

// ---------------------------------------------------------------- punten

/**
 * Overwinningspunten van een speler.
 *
 * Bouwwerken tellen mee (dorp 1, stad 2 — de stad vervangt het dorp), plus alle
 * uitbreidingen die punten opleveren, plus de twee speelfiguren.
 */
export function victoryPoints(
  player: PlayerState,
  cardPoints: (cardId: number) => number,
  buildingPoints: (kind: BuildingKind) => number,
  tokens: { knight: Seat | undefined; windmill: Seat | undefined },
): number {
  let total = 0;
  for (const building of player.principality.buildings) {
    total += buildingPoints(building.kind);
    for (const side of ['boven', 'onder'] as const) {
      for (const cardId of building.expansions[side]) total += cardPoints(cardId);
    }
  }
  if (tokens.knight === player.seat) total += 1;
  if (tokens.windmill === player.seat) total += 1;
  return total;
}

/** Heeft deze speler genoeg punten om te winnen? */
export function hasWon(points: number): boolean {
  return points >= LIMITS.victoryPoints;
}

/**
 * Naar welke kant het vorstendom groeide toen dit dorp erbij kwam.
 *
 * De landschappen komen aan de buitenkant te liggen, dus dat is de kolom één verder van
 * het midden af. Het dorp staat er al als we dit vragen, vandaar dat het zelf niet meetelt.
 */
export function groeirichting(player: PlayerState, column: number): 1 | -1 {
  const anderen = player.principality.buildings
    .map((b) => b.column)
    .filter((c) => c !== column);
  return anderen.length === 0 || column > Math.max(...anderen) ? 1 : -1;
}

/** Het kaartnummer van de Verkenner in deze hand, of niets. */
export function verkennerInHand(player: PlayerState): number | undefined {
  return player.hand.find((id) => actionCardName(id) === 'Verkenner');
}

/**
 * Hoeveel landschappen deze speler er nog bij mag krijgen — hoogstens twee per dorp.
 *
 * Nul betekent: zijn vorstendom zit vol. Dan valt er ook niets te verkennen.
 */
/**
 * Hoeveel er van dit bouwwerk nog in de gedeelde voorraad ligt.
 *
 * "7 straten. 5 dorpen. 7 steden." (handboek p. 16) — één voorraad voor béíde spelers.
 * Elke speler begint daarnaast met 2 dorpen en 1 straat uit zijn startkaarten. Een stad
 * haalt een dorp van tafel en daarmee komt die vanzelf terug in de voorraad: tellen wat
 * er stáát is dus genoeg. Uit het vijfde echte potje: "je hebt al vijf dorpen" terwijl
 * de tegenstander er nul had bijgebouwd — de voorraad had er nog twee.
 */
export function voorraadBouwwerken(
  een: Principality,
  twee: Principality,
  kind: BuildingKind | 'straat',
): number {
  const opTafel =
    kind === 'straat'
      ? een.roads.length + twee.roads.length
      : countBuildings(een, kind) + countBuildings(twee, kind);
  const start =
    kind === 'straat' ? 2 * LIMITS.startStraten : kind === 'dorp' ? 2 * LIMITS.startDorpen : 0;
  const voorraad =
    kind === 'straat'
      ? LIMITS.stratenVoorraad
      : kind === 'dorp'
        ? LIMITS.dorpenVoorraad
        : LIMITS.stedenVoorraad;
  return Math.max(0, voorraad + start - opTafel);
}

/**
 * Legt landschappen bij een nieuw dorp, aan de buitenkant.
 *
 * De kaarten komen mee als lijst — van de stapel bij een gewoon dorp, of zelf uitgezocht
 * met de Verkenner. "De speler beslist zelf, welk land hij boven en welk land hij onder
 * legt" (handboek p. 9): `eersteZijde` is die beslissing — de eerste kaart komt daar, de
 * tweede vanzelf aan de andere kant.
 *
 * Er is geen grens per speler: de enige grens is de gedeelde trekstapel van elf landen
 * (p. 16). Hier lag eerst een eigen maximum van elf landschappen per spéler — dat kende
 * het boekje niet, en het snoeide precies het twaalfde land weg waar de stapel nog wel
 * in voorzag.
 */
export function legLandschappen(
  player: PlayerState,
  buitenkolom: number,
  kaarten: readonly StartLandschap[],
  eersteZijde: Side = 'boven',
): PlayerState {
  const p = player.principality;
  const zijden: readonly Side[] = eersteZijde === 'boven' ? ['boven', 'onder'] : ['onder', 'boven'];
  const nieuw: LandscapeTile[] = kaarten.map((kaart, i) => ({
    landscape: kaart.landscape,
    column: buitenkolom,
    side: zijden[i] ?? 'onder',
    dieNumber: kaart.dieNumber,
    // Nieuwe landschappen komen leeg op tafel.
    resources: 0,
  }));
  return { ...player, principality: { ...p, landscapes: [...p.landscapes, ...nieuw] } };
}
